import React from 'react';
import { MAX_INVESTMENT_AMOUNT_USD } from '../../../constants/profileFieldLimits';
import { formatCompactAmount } from '../../../utils/formatCompactAmount';

interface AmountSliderProps {
  value: number;
  onChange: (amount: number) => void;
  min?: number;
  max?: number;
  step?: number;
  multiplier?: number; // e.g., 10x
  currency?: string;
  isMultiplierActive?: boolean;
  onMultiplierToggle?: () => void;
  /**
   * When true (default), active +10x multiplies the displayed value (revenue slider).
   * When false, value is already a true amount and +10x only unlocks a higher ceiling.
   */
  multiplyDisplay?: boolean;
  /** Investor-style typed amount field (startup investment only). */
  showManualInput?: boolean;
  inputLabel?: string;
}

const formatNumber = (num: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(num);

const AmountSlider: React.FC<AmountSliderProps> = ({
  value,
  onChange,
  min = 0,
  max = MAX_INVESTMENT_AMOUNT_USD,
  step = 1000,
  multiplier = 1,
  currency = 'EUR',
  isMultiplierActive = false,
  onMultiplierToggle,
  multiplyDisplay = true,
  showManualInput = false,
  inputLabel,
}) => {
  const scaleActive = Boolean(multiplyDisplay && isMultiplierActive && multiplier > 1);
  const displayed = scaleActive ? value * multiplier : value;
  const sliderValue = value <= 0 ? min : Math.min(max, Math.max(min, value));
  const emptyManualInput = showManualInput && value <= 0;

  const [inputAmount, setInputAmount] = React.useState(
    emptyManualInput ? '' : formatNumber(displayed)
  );
  const amountFocused = React.useRef(false);

  React.useEffect(() => {
    if (!amountFocused.current) {
      setInputAmount(showManualInput && displayed <= 0 ? '' : formatNumber(displayed));
    }
  }, [displayed, showManualInput]);

  const parseToRaw = (raw: string): number | null => {
    const cleaned = raw.replace(/[,\s]/g, '');
    const parsed = parseFloat(cleaned);
    if (isNaN(parsed) || parsed < 0) return null;
    return scaleActive ? parsed / multiplier : parsed;
  };

  const applyTypedAmount = (raw: string) => {
    const rawValue = parseToRaw(raw);
    if (rawValue === null) return null;
    const stepped = Math.round(rawValue / step) * step;
    // Keep 0 / below-minimum while typing; save-time validation enforces the floor.
    return Math.max(0, Math.min(max, stepped));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputAmount(val);
    const next = applyTypedAmount(val);
    if (next !== null) onChange(next);
  };

  const handleInputBlur = () => {
    amountFocused.current = false;
    if (showManualInput && inputAmount.trim() === '') {
      onChange(0);
      setInputAmount('');
      return;
    }
    const next = applyTypedAmount(inputAmount);
    if (next !== null) {
      onChange(next);
      setInputAmount(formatNumber(scaleActive ? next * multiplier : next));
    } else {
      setInputAmount(showManualInput && displayed <= 0 ? '' : formatNumber(displayed));
    }
  };

  const typedRaw = parseToRaw(inputAmount);
  const typedDisplayed = typedRaw === null ? null : scaleActive ? typedRaw * multiplier : typedRaw;
  const showMinimumHint =
    showManualInput &&
    min > 0 &&
    ((typedDisplayed !== null && typedDisplayed < min) || displayed < min);
  const minimumLabel = formatNumber(scaleActive ? min * multiplier : min);
  const fieldLabel = inputLabel || `Amount asked ${currency.toUpperCase()}`;

  return (
    <div className="space-y-2">
      {showManualInput && (
        <div className="w-48">
          <p className="text-white font-semibold text-xs mb-1 uppercase text-center">
            {fieldLabel}
          </p>
          <input
            type="text"
            inputMode="numeric"
            value={inputAmount}
            onChange={handleInputChange}
            onFocus={() => {
              amountFocused.current = true;
            }}
            onBlur={handleInputBlur}
            placeholder="0"
            className="w-full bg-transparent text-white text-sm rounded px-3 py-2 border border-cyan-500 focus:outline-none"
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
        <div className="flex-grow">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={sliderValue}
            onChange={e => onChange(Number(e.target.value))}
            className="w-full h-2 bg-gray-500 rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: '#06b6d4' }}
          />
        </div>
        {multiplier !== 1 && (
          <button
            type="button"
            onClick={onMultiplierToggle}
            className={`px-3 py-1 text-sm rounded-md bg-gray-700 text-white border transition-colors ${
              isMultiplierActive
                ? 'border-emerald-400 shadow-[0_0_0_2px_rgba(146,208,80,0.3)_inset]'
                : 'border-gray-600'
            }`}
          >
            +{multiplier}x
          </button>
        )}
        <div className="min-w-0 sm:min-w-[180px] text-left sm:text-right text-white font-semibold uppercase">
          {showManualInput
            ? `Amount: ${formatNumber(displayed)} ${currency.toUpperCase()}`
            : `${formatCompactAmount(displayed)} ${currency.toUpperCase()}`}
        </div>
      </div>

      {showMinimumHint && (
        <p className="text-yellow-500 text-sm mt-2 text-left">Minimum investment: {minimumLabel}</p>
      )}
    </div>
  );
};

export default AmountSlider;
